import { useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { StyleTag } from "./Signup";

export default function Verify() {
  const { verify } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const location = useLocation();
  const [email, setEmail] = useState(sp.get("email") || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState(location.state?.devCode || null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await verify(email, code);
      toast.success("Verified. Let's set you up.");
      nav(user.onboarded ? "/chat" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-code", { email, password: "x" });
      if (data.dev_verification_code) {
        setDevCode(data.dev_verification_code);
        toast.info("New code ready below.");
      } else {
        setDevCode(null);
        toast.success("New code sent to your email.");
      }
    } catch (err) {
      toast.error("Couldn't resend");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <Nav />
      <div className="max-w-md mx-auto px-6 py-12">
        <span className="text-xs uppercase tracking-[0.22em] text-[#2E9D96] font-semibold">Step 2 of 2</span>
        <h1 className="font-display text-4xl text-slate-800 mt-2">Confirm it's you.</h1>
        <p className="text-slate-600 mt-2">
          {devCode ? "Enter the 6-digit code below to verify your account." : "We sent a 6-digit code to your email — pop it in below."}
        </p>

        {devCode && (
          <div className="mt-6 rounded-2xl border border-[#2E9D96]/30 bg-[#2E9D96]/[0.06] p-5 text-center" data-testid="verify-dev-code">
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Your verification code</p>
            <p className="font-display text-4xl tracking-[0.4em] text-[#2E9D96] mt-2">{devCode}</p>
            <p className="text-xs text-slate-500 mt-2">Email delivery isn't switched on yet, so here's your code directly.</p>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-5">
          <label className="block">
            <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input zaf-focus mt-2" data-testid="verify-email-input" />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Verification code</span>
            <input required value={code} onChange={(e) => setCode(e.target.value)} className="input zaf-focus mt-2 tracking-[0.5em] text-center font-semibold text-lg" maxLength={6} data-testid="verify-code-input" />
          </label>
          <button data-testid="verify-submit" disabled={loading} className="w-full bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-60 text-white font-semibold rounded-full py-3 transition-colors">
            {loading ? "Verifying…" : "Verify"}
          </button>
          <button type="button" onClick={resend} className="w-full text-sm text-slate-500 hover:text-[#2E9D96]" data-testid="verify-resend">
            Resend code
          </button>
        </form>
      </div>
      <StyleTag />
    </div>
  );
}
